import React, { useContext } from "react";
import {
  Button,
  ButtonGroup,
  CopyToClipboard,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import CodeView from "@amzn/awsui-code-view/code-view";
import {
  generateDirectCloudWatchUrl,
  generateMerlonLogsUrl,
} from "../../helpers/logUtils";
import { Stage } from "../../lusca-search-api/generated-src";
import { DevUserContext } from "../../contexes/OrderIdContext";

interface ViewLogsProps {
  stage: Stage;
  /** Dev stack the logs belong to; defaults to the page's Dev user. */
  user?: string;
  logGroups: string[];
  logQuery: string;
  dateRange?: [Date, Date];
  timestamp?: Date;
  windowMinutes?: number;
  minimal?: boolean;
}

const ViewLogs: React.FC<ViewLogsProps> = ({
  stage,
  user,
  logGroups,
  logQuery,
  dateRange,
  timestamp,
  windowMinutes = 5,
  minimal = false,
}) => {
  const getDateRangeArgs = () => {
    if (dateRange) {
      return dateRange;
    }
    if (timestamp) {
      const startDate = new Date(timestamp);
      const endDate = new Date(timestamp);
      startDate.setMinutes(startDate.getMinutes() - windowMinutes);
      endDate.setMinutes(endDate.getMinutes() + windowMinutes);
      return [startDate, endDate] as [Date, Date];
    }
    return [];
  };

  const contextUser = useContext(DevUserContext);
  const devUser = user ?? contextUser;
  const hasQuery = Boolean(logQuery);

  const generateUrl = () => {
    const [startTime, endTime] = getDateRangeArgs();
    return generateMerlonLogsUrl(
      stage,
      logGroups,
      logQuery,
      startTime,
      endTime,
      devUser
    );
  };

  const generateDirectUrl = () => {
    const dateRangeArgs = getDateRangeArgs();
    return generateDirectCloudWatchUrl(
      logGroups,
      logQuery,
      ...(dateRangeArgs.length ? dateRangeArgs : [])
    );
  };

  const popoverContent = (
    <SpaceBetween size="s">
      <CodeView
        content={logQuery}
        actions={
          <CopyToClipboard
            copyButtonAriaLabel="Copy query"
            copyErrorText="Query failed to copy"
            copySuccessText="Query copied"
            textToCopy={logQuery}
          />
        }
      />
      <SpaceBetween size="m" direction="horizontal">
        <Button
          href={hasQuery ? generateDirectUrl() : undefined}
          target="_blank"
          rel="noopener noreferrer"
          iconName="external"
          variant="normal"
        >
          Open (without login)
        </Button>
        <Button
          href={hasQuery ? generateUrl() : undefined}
          target="_blank"
          rel="noopener noreferrer"
          iconName="external"
          variant="primary"
        >
          Open (with login)
        </Button>
      </SpaceBetween>
      <CodeView content={logGroups.join("\n")} />
    </SpaceBetween>
  );

  if (minimal) {
    return (
      <SpaceBetween alignItems="center" direction="horizontal" size="xxs">
        <Popover
          dismissButton={false}
          fixedWidth={false}
          position="bottom"
          size="large"
          triggerType="custom"
          content={popoverContent}
        >
          <Button
            iconName="status-info"
            variant="icon"
            disabled={!hasQuery}
            ariaLabel="View query"
          />
        </Popover>
        <ButtonGroup
          items={[
            {
              type: "icon-button",
              id: "copy-query",
              iconName: "copy",
              text: "Copy query",
              disabled: !hasQuery,
              popoverFeedback: (
                <StatusIndicator type="success">Query copied</StatusIndicator>
              ),
            },
            {
              type: "icon-button",
              id: "view-logs",
              iconName: "external",
              text: "Open in Cloudwatch",
              disabled: !hasQuery,
            },
          ]}
          onItemClick={({ detail }) => {
            if (detail.id === "copy-query") {
              navigator.clipboard.writeText(logQuery);
            } else if (detail.id === "view-logs") {
              window.open(generateUrl(), "_blank", "noopener noreferrer");
            }
          }}
          variant="icon"
          ariaLabel="Log actions"
        />
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween alignItems="center" direction="horizontal" size="xxs">
      <Popover
        dismissButton={false}
        fixedWidth={false}
        position="bottom"
        size="large"
        triggerType="custom"
        content={popoverContent}
      >
        <Button
          iconName="status-info"
          variant="inline-icon"
          disabled={!hasQuery}
          ariaLabel="View query"
        />
      </Popover>
      <Button
        href={hasQuery ? generateUrl() : undefined}
        target="_blank"
        rel="noopener noreferrer"
        iconName="external"
        disabled={!hasQuery}
      >
        View Logs
      </Button>
    </SpaceBetween>
  );
};

export default ViewLogs;
